import React, { useState, useMemo } from 'react';
import type { Node, Edge } from 'reactflow';
import { X, Play, RotateCcw, ChevronRight } from 'lucide-react';
import type { FlowNodeData, EdgeCondition } from './types';

interface FlowPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodes: Node<FlowNodeData>[];
  edges: Edge[];
}

interface SimulationStep {
  nodeId: string;
  nodeType: string;
  label: string;
  action: string;
  output?: string;
}

export const FlowPreviewModal: React.FC<FlowPreviewModalProps> = ({
  isOpen,
  onClose,
  nodes,
  edges,
}) => {
  const [simulationSteps, setSimulationSteps] = useState<SimulationStep[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [variables, setVariables] = useState<Record<string, string>>({
    customer_name: 'João Silva',
    last_response: '',
    dias_cancelamento: '5',
  });
  const [isRunning, setIsRunning] = useState(false);

  const startNode = useMemo(() => {
    return nodes.find((n) => n.data.nodeType === 'START');
  }, [nodes]);

  const evaluateCondition = (condition: EdgeCondition, vars: Record<string, string>): boolean => {
    const fieldValue = vars[condition.field] || '';
    const conditionValue = condition.value;

    switch (condition.operator) {
      case 'equals':
        return fieldValue.toLowerCase() === conditionValue.toLowerCase();
      case 'not_equals':
        return fieldValue.toLowerCase() !== conditionValue.toLowerCase();
      case 'contains':
        return fieldValue.toLowerCase().includes(conditionValue.toLowerCase());
      case 'not_contains':
        return !fieldValue.toLowerCase().includes(conditionValue.toLowerCase());
      case 'starts_with':
        return fieldValue.toLowerCase().startsWith(conditionValue.toLowerCase());
      case 'ends_with':
        return fieldValue.toLowerCase().endsWith(conditionValue.toLowerCase());
      default:
        return false;
    }
  };

  const interpolateVariables = (text: string, vars: Record<string, string>): string => {
    let result = text;
    Object.keys(vars).forEach((key) => {
      const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
      result = result.replace(regex, vars[key]);
    });
    return result;
  };

  const getNextNode = (currentNodeId: string, vars: Record<string, string>): Node<FlowNodeData> | null => {
    const outgoingEdges = edges
      .filter((e) => e.source === currentNodeId)
      .sort((a, b) => (b.data?.priority || 0) - (a.data?.priority || 0));

    for (const edge of outgoingEdges) {
      if (edge.data?.condition) {
        if (evaluateCondition(edge.data.condition, vars)) {
          return nodes.find((n) => n.id === edge.target) || null;
        }
      } else {
        // No condition, this is the default path
        return nodes.find((n) => n.id === edge.target) || null;
      }
    }

    return null;
  };

  const runSimulation = () => {
    if (!startNode) {
      alert('Nenhum nó START encontrado!');
      return;
    }

    setIsRunning(true);
    setSimulationSteps([]);
    setCurrentStepIndex(-1);

    const steps: SimulationStep[] = [];
    const tempVars = { ...variables };
    let currentNode: Node<FlowNodeData> | null = startNode;
    let maxSteps = 100; // Prevent infinite loops
    let stepCount = 0;

    while (currentNode && stepCount < maxSteps) {
      const nodeData = currentNode.data;
      const step: SimulationStep = {
        nodeId: currentNode.id,
        nodeType: nodeData.nodeType,
        label: nodeData.label || nodeData.nodeType,
        action: '',
      };

      switch (nodeData.nodeType) {
        case 'START':
          step.action = 'Iniciando fluxo';
          break;

        case 'SEND_MESSAGE':
          const message = interpolateVariables(nodeData.config.message || '', tempVars);
          step.action = 'Enviando mensagem';
          step.output = message;
          break;

        case 'SEND_TEMPLATE':
          step.action = `Enviando template: ${nodeData.config.templateName || 'N/A'}`;
          step.output = `Template: ${nodeData.config.templateName}`;
          break;

        case 'WAIT_RESPONSE':
          step.action = 'Aguardando resposta do cliente';
          step.output = `Resposta simulada: "${tempVars.last_response}"`;
          break;

        case 'CONDITION':
          step.action = 'Avaliando condição';
          const nextNode = getNextNode(currentNode.id, tempVars);
          step.output = nextNode ? `Seguindo para: ${nextNode.data.label || nextNode.data.nodeType}` : 'Nenhum caminho correspondente';
          break;

        case 'TRANSFER_DEPARTMENT':
          step.action = `Transferindo para departamento`;
          step.output = `Departamento: ${nodeData.config.departmentName || 'N/A'}`;
          break;

        case 'UPDATE_STATUS':
          step.action = `Atualizando status`;
          step.output = `Novo status: ${nodeData.config.status || 'N/A'}`;
          break;

        case 'SET_VARIABLE':
          const varName = nodeData.config.variableName;
          const varValue = interpolateVariables(nodeData.config.value || '', tempVars);
          tempVars[varName] = varValue;
          step.action = `Definindo variável`;
          step.output = `${varName} = "${varValue}"`;
          break;

        case 'CLOSE_CONVERSATION':
          step.action = 'Encerrando conversa';
          step.output = nodeData.config.reason ? `Motivo: ${nodeData.config.reason}` : 'Conversa encerrada';
          break;

        case 'END':
          step.action = 'Finalizando fluxo';
          break;
      }

      steps.push(step);

      // Check if this is an end node
      if (nodeData.nodeType === 'END' || nodeData.nodeType === 'CLOSE_CONVERSATION') {
        break;
      }

      // Get next node
      currentNode = getNextNode(currentNode.id, tempVars);
      stepCount++;
    }

    if (stepCount >= maxSteps) {
      steps.push({
        nodeId: 'error',
        nodeType: 'ERROR',
        label: 'Erro',
        action: 'Loop infinito detectado! A simulação foi interrompida.',
      });
    }

    setSimulationSteps(steps);
    setCurrentStepIndex(0);
  };

  const resetSimulation = () => {
    setSimulationSteps([]);
    setCurrentStepIndex(-1);
    setIsRunning(false);
  };

  const nextStep = () => {
    if (currentStepIndex < simulationSteps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const prevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Play className="w-6 h-6 text-green-600 dark:text-green-400" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Simulação de Fluxo
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Variables Input */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
              Variáveis de Teste
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  customer_name
                </label>
                <input
                  type="text"
                  value={variables.customer_name}
                  onChange={(e) => setVariables({ ...variables, customer_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                  disabled={isRunning}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  last_response
                </label>
                <input
                  type="text"
                  value={variables.last_response}
                  onChange={(e) => setVariables({ ...variables, last_response: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="Ex: sim, não"
                  disabled={isRunning}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  dias_cancelamento
                </label>
                <input
                  type="text"
                  value={variables.dias_cancelamento}
                  onChange={(e) => setVariables({ ...variables, dias_cancelamento: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                  disabled={isRunning}
                />
              </div>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex gap-3 mb-6">
            {!isRunning ? (
              <button
                onClick={runSimulation}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Play className="w-4 h-4" />
                Executar Simulação
              </button>
            ) : (
              <button
                onClick={resetSimulation}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <RotateCcw className="w-4 h-4" />
                Resetar
              </button>
            )}
          </div>

          {/* Simulation Steps */}
          {simulationSteps.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                Passos da Execução ({simulationSteps.length} passos)
              </h4>
              {simulationSteps.map((step, index) => (
                <div
                  key={index}
                  className={`
                    border rounded-lg p-4 transition-all
                    ${index === currentStepIndex
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                      : index < currentStepIndex
                      ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10'
                      : 'border-gray-200 dark:border-gray-700 opacity-50'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    <div className={`
                      flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                      ${index === currentStepIndex
                        ? 'bg-blue-600 text-white'
                        : index < currentStepIndex
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                      }
                    `}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {step.label}
                        </span>
                        <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                          {step.nodeType}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                        {step.action}
                      </p>
                      {step.output && (
                        <div className="mt-2 p-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-sm font-mono">
                          {step.output}
                        </div>
                      )}
                    </div>
                    {index === currentStepIndex && (
                      <ChevronRight className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-pulse" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {simulationSteps.length === 0 && !isRunning && (
            <div className="text-center py-12">
              <Play className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                Configure as variáveis de teste e clique em "Executar Simulação" para visualizar o fluxo.
              </p>
            </div>
          )}
        </div>

        {/* Navigation Footer */}
        {isRunning && simulationSteps.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <button
                onClick={prevStep}
                disabled={currentStepIndex <= 0}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Anterior
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Passo {currentStepIndex + 1} de {simulationSteps.length}
              </span>
              <button
                onClick={nextStep}
                disabled={currentStepIndex >= simulationSteps.length - 1}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
